import {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {Link} from "react-router-dom";
import {css} from "@emotion/react";
import {elevation} from "@pg-design/elevation";
import {Col, Container, Row} from "@pg-design/grid";
import {
    borderRadius,
    calculateRemSize,
    flex,
    flexAlignCenter,
    flexDirection,
    listUnStyled,
    mb,
    onDesktop,
    p,
    ph,
    w100
} from "@pg-design/helpers-css";
import {CheckIcon, ChevronRightIcon} from "@pg-design/icons";
import {Link as ExternalLink} from "@pg-design/link";
import {Picture} from "@pg-design/picture";
import {Text} from "@pg-design/text";

import {chartsRoutes} from "../../../common/app/routing/charts_routes";
import {breakpoints} from "../../../common/app/styles/breakpoints";
import {flexShrink} from "../../../components/css_helpers/flex";
import {LANDING_WIDE_COL_PROPS} from "./landingMarketNews.constants";

import insightImg from "../assets/solutions/insight.png";
import monitoringImg from "../assets/solutions/monitoring.png";
import platformImg from "../assets/solutions/platform.png";

const DESKTOP_GAP = 3;
const CARD_IMAGE_HEIGHT_PX = 160;
const MOBILE_CARD_IMAGE = {width: 357, height: CARD_IMAGE_HEIGHT_PX};
const DESKTOP_CARD_IMAGE = {width: 255, height: CARD_IMAGE_HEIGHT_PX};
const DESKTOP_BREAKPOINT_PX = parseInt(breakpoints.sm, 10);
const CHECK_COLOR = "#23232D";
const CHEVRON_BG = "#D9D9D9";

type BenefitKeys = string[];
type CardIndex = 1 | 2 | 3 | 4 | 5;

interface ISolutionCard {
    titleKey: string;
    descriptionKey: string;
    benefitKeys: BenefitKeys;
    url: string;
    isExternal?: boolean;
    mobilePicture: string;
    desktopPicture: string;
}

const benefitKeysFor = (cardIndex: CardIndex): BenefitKeys => [
    `landing.solutions.card${cardIndex}.benefit_1`,
    `landing.solutions.card${cardIndex}.benefit_2`,
    `landing.solutions.card${cardIndex}.benefit_3`,
    `landing.solutions.card${cardIndex}.benefit_4`
];

// TODO: swap back to routes.newshub.root once BIG DATA Insight is migrated to this domain
const INSIGHT_TEMP_URL = "https://insight.rynekpierwotny.pl/";

const buildSolutions = (language: string): ISolutionCard[] => {
    const routes = chartsRoutes[language];

    return [
        {
            titleKey: "landing.solutions.card1.title",
            descriptionKey: "landing.solutions.card1.description",
            benefitKeys: benefitKeysFor(1),
            url: routes.solutionsAnalyticalPlatform,
            mobilePicture: platformImg,
            desktopPicture: platformImg
        },
        {
            titleKey: "landing.solutions.card2.title",
            descriptionKey: "landing.solutions.card2.description",
            benefitKeys: benefitKeysFor(2),
            url: routes.monitoring.root,
            mobilePicture: monitoringImg,
            desktopPicture: monitoringImg
        },
        {
            titleKey: "landing.solutions.card3.title",
            descriptionKey: "landing.solutions.card3.description",
            benefitKeys: benefitKeysFor(3),
            url: INSIGHT_TEMP_URL,
            isExternal: true,
            mobilePicture: insightImg,
            desktopPicture: insightImg
        }
    ];
};

interface ISolutionCardProps {
    titleKey: string;
    descriptionKey: string;
    benefitKeys: BenefitKeys;
    url: string;
    isExternal?: boolean;
    mobilePicture: string;
    desktopPicture: string;
}

const SolutionCard = ({
    titleKey,
    descriptionKey,
    benefitKeys,
    url,
    isExternal,
    mobilePicture,
    desktopPicture
}: ISolutionCardProps) => {
    const {t} = useTranslation();

    const cardContentJsx = (
        <div css={cardWrapper}>
            <article css={cardBody}>
                <div css={imageWrapper}>
                    <Picture
                        css={cardPicture}
                        sources={[
                            {minWidthPX: 0, src: mobilePicture, ...MOBILE_CARD_IMAGE},
                            {minWidthPX: DESKTOP_BREAKPOINT_PX, src: desktopPicture, ...DESKTOP_CARD_IMAGE}
                        ]}
                        alt={t(titleKey)}
                        loading="lazy"
                    />
                </div>

                <div css={cardContent}>
                    <Text css={mb(1)} variant="headline_4" as="h3">
                        {t(titleKey)}
                    </Text>

                    <Text css={description} variant="body_copy_1">
                        {t(descriptionKey)}
                    </Text>

                    <ul css={benefitsList}>
                        {benefitKeys.map((key) => (
                            <li css={benefitItem} key={key}>
                                <CheckIcon
                                    size="1.6"
                                    wrapperSize="1.6"
                                    wrapperColor="transparent"
                                    fill={CHECK_COLOR}
                                    css={checkIcon}
                                />
                                <Text variant="body_copy_2">{t(key)}</Text>
                            </li>
                        ))}
                    </ul>

                    <div css={cardFooter} aria-hidden>
                        <ChevronRightIcon size="1.6" wrapperSize="3.2" wrapperColor={CHEVRON_BG} fill="#fff" />
                    </div>
                </div>
            </article>
        </div>
    );

    if (isExternal) {
        return (
            <ExternalLink href={url} target="_blank" rel="noopener noreferrer" css={category}>
                {cardContentJsx}
            </ExternalLink>
        );
    }

    return (
        <Link to={url} css={category}>
            {cardContentJsx}
        </Link>
    );
};

export const BigDataSolutions = () => {
    const {t, i18n} = useTranslation();

    const solutions = useMemo(() => buildSolutions(i18n.language), [i18n.language]);

    return (
        <Container as="section" css={section}>
            <Row>
                <Col {...LANDING_WIDE_COL_PROPS}>
                    <Text css={title} as="h2" variant="headline_1">
                        {t("landing.solutions.title")}
                    </Text>

                    <Text css={subtitle} variant="headline_4">
                        {t("landing.solutions.subtitle")}
                    </Text>

                    <div css={categories}>
                        {solutions.map((solution) => (
                            <SolutionCard key={solution.titleKey} {...solution} />
                        ))}
                    </div>
                </Col>
            </Row>
        </Container>
    );
};

const section = css`
    ${w100};
    ${mb(4)};
    ${ph(1)};
`;

const title = css`
    margin: 0;
`;

const subtitle = css`
    font-weight: 400;
    ${mb(4)};

    ${onDesktop(css`
        ${mb(5)};
    `)};
`;

const categories = css`
    ${flex()};
    ${flexDirection("column")};
    ${flexAlignCenter};
    gap: ${calculateRemSize(2)};
    ${w100};

    @media (min-width: ${breakpoints.sm}) {
        flex-direction: row;
        align-items: stretch;
        gap: ${calculateRemSize(DESKTOP_GAP)};
    }
`;

const category = css`
    ${w100};
    min-width: 0;
    text-decoration: none;
    color: inherit;
    cursor: pointer;

    &:hover,
    &:focus {
        text-decoration: none;
        color: inherit;
    }

    @media (min-width: ${breakpoints.sm}) {
        flex: 1 1 0;
    }
`;

const cardWrapper = css`
    ${flex()};
    ${flexDirection("column")};
    ${w100};
    background: #fff;
    ${borderRadius(2)};
    overflow: hidden;
    ${elevation(3)};

    @media (min-width: ${breakpoints.sm}) {
        height: 100%;
    }
`;

const cardBody = css`
    ${flex()};
    ${flexDirection("column")};
    flex: 1 1 auto;
    ${w100};
`;

const imageWrapper = css`
    ${w100};
    flex: 0 0 ${CARD_IMAGE_HEIGHT_PX}px;
    height: ${CARD_IMAGE_HEIGHT_PX}px;
    overflow: hidden;
`;

const cardContent = css`
    ${flex()};
    ${flexDirection("column")};
    flex: 1 0 auto;
    ${p(2)};
    ${w100};
    min-width: 0;
`;

const cardPicture = css`
    ${w100};
    height: 100%;

    img {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`;

const description = css`
    ${mb(2)};
`;

const benefitsList = css`
    ${listUnStyled};
    flex: 1 1 auto;
    margin: 0;
`;

const benefitItem = css`
    ${flexAlignCenter};
    gap: ${calculateRemSize(1)};
    ${mb(1)};

    &:last-of-type {
        margin-bottom: 0;
    }
`;

const checkIcon = css`
    ${flexShrink(0)};
`;

const cardFooter = css`
    ${flex()};
    justify-content: flex-end;
    margin-top: auto;
`;
